import React from "react";
import { ArrowUpRight } from "lucide-react";
import { resolveImageUrl } from "../lib/api";
import { useSettings } from "../context/SettingsContext";

/**
 * Newsletter promo at the very end of each blog post.
 * Centered column, no card/box/border, page background.
 *
 * Sequence (top → bottom):
 *   1. Image (300×300, no frame)
 *   2. Title
 *   3. Tagline
 *   4. "1,049 connections are subscribed"
 *   5. "1,168 subscribers"
 *   6. CTA button
 */
export const CommunityCard = () => {
  const { settings } = useSettings();
  if (settings?.community_enabled === false) return null;

  const image = resolveImageUrl(settings.community_image || "");
  const title = settings.community_title || "";
  const tagline = settings.community_tagline || "";
  const social = settings.community_social_text || "";
  const subs = settings.community_subscriber_text || "";
  const ctaUrl = settings.community_cta_url || "";
  const ctaLabel = settings.community_cta_label || "Subscribe";

  if (!title && !tagline && !image) return null;

  return (
    <section
      data-testid="community-card"
      className="mt-4 pt-2 flex flex-col items-center text-center"
    >
      {/* 1. Image — 75×75, horizontal line passing behind it on both sides */}
      <div className="relative w-full flex items-center justify-center">
        <span
          className="absolute left-0 right-0 top-1/2 h-px bg-black/15"
          aria-hidden
        />
        {image ? (
          <img
            data-testid="community-image"
            src={image}
            alt={title}
            className="relative w-[75px] h-[75px] object-contain bg-white px-1.5"
            draggable={false}
          />
        ) : (
          <div className="relative w-[75px] h-[75px] grid place-items-center text-center px-1.5 bg-white">
            <span className="font-display font-bold text-[7px] uppercase tracking-[0.16em] text-zinc-400 leading-tight">
              {title || "Upload"}
            </span>
          </div>
        )}
      </div>

      {/* 2. Title */}
      {title && (
        <h3
          data-testid="community-title"
          className="font-display font-bold text-xs md:text-sm tracking-tight leading-tight mt-1.5"
        >
          {title}
        </h3>
      )}

      {/* 3. Tagline */}
      {tagline && (
        <p
          data-testid="community-tagline"
          className="text-zinc-600 text-[10px] md:text-xs leading-snug max-w-xs mt-1"
        >
          {tagline}
        </p>
      )}

      {/* 4. Social proof line */}
      {social && (
        <p
          data-testid="community-social"
          className="text-zinc-500 text-[10px] mt-1.5"
        >
          {social}
        </p>
      )}

      {/* 5. Subscriber count */}
      {subs && (
        <p
          data-testid="community-subs"
          className="text-zinc-500 text-[10px] mt-0.5"
        >
          {subs}
        </p>
      )}

      {/* 6. CTA */}
      {ctaUrl && (
        <a
          data-testid="community-cta"
          href={ctaUrl}
          target="_blank"
          rel="noreferrer"
          className="group inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 bg-[#0A0A0A] text-white font-display text-[8px] font-bold uppercase tracking-[0.18em] hover:bg-[#002FA7] transition-colors"
        >
          {ctaLabel}
          <ArrowUpRight
            size={9}
            strokeWidth={2.5}
            className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
          />
        </a>
      )}
    </section>
  );
};

export default CommunityCard;
